'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { REGISTRATION_DETAILS } from '@/lib/company';
import { useI18n } from '@/i18n/LocaleProvider';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = section.querySelectorAll('.stagger-child');
    children.forEach((child, i) => {
      (child as HTMLElement).style.transitionDelay = `${i * 100}ms`;
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto scroll-mt-24">
      
      <div className="glass-card rounded-3xl overflow-hidden border border-border border-top-glow border-top-glow-gold">
          <div className="p-8 md:p-12 max-w-3xl">
            <div>
              <div className="stagger-child">
                <span className="text-xs font-bold tracking-widest uppercase text-accent mb-2 block">{t.about.eyebrow}</span>
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-foreground mb-6 leading-tight">
                  {t.about.title}
                </h2>
              </div>

              <div className="stagger-child">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t.about.p1}
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {t.about.p2}
                </p>
              </div>

              {/* Registration details */}
              <dl className="stagger-child flex flex-wrap gap-x-6 gap-y-2 text-sm mb-8">
                {REGISTRATION_DETAILS.map((item) =>
                <div key={item.label} className="flex gap-1.5">
                    <dt className="font-semibold text-foreground">{item.label}:</dt>
                    <dd className="text-muted-foreground">{item.value || '—'}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="stagger-child flex flex-col sm:flex-row gap-3">
              <Link
                href="/category"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                
                {t.about.browseProducts}
              </Link>
            </div>
          </div>
      </div>
    </section>);

}